import { makeAutoObservable, reaction } from "mobx";
import { RootStore } from "../RootStore";
import { CourtId, CourtType, DateInfo } from "../../types/types";
import {
  SpecificBookingSlotsResponse,
  SpecificBookingSlot,
  Country,
  CreateLeagueMatchPayload,
  CourtLocation,
} from "../../apis/types";
import {
  days,
  months,
  REQUEST_PLAYER_DATES_LIST_COUNT,
} from "../../utils/constants";
import { Strings } from "../../utils/strings";
import { FrontDeskApi } from "../../apis/frontDesk.api";
import { getDateInYYYYMMDDFormat } from "../../utils/utils";

export class CreateLeagueMatchStore {
  rootStore: RootStore;
  modalData?: {
    visible: boolean;
    title: string;
    children: string;
    btnText?: string;
    btnVariant?: string;
    onClose?: () => void;
    onAccept?: () => void;
    headerIcon?: React.ReactNode;
  };
  isLoading: boolean = false;
  nextSixDays: DateInfo[] = [];
  selectedDate: DateInfo | null = null;
  timeSlotsResponse: SpecificBookingSlotsResponse | null = null;
  timeSlots: SpecificBookingSlot[] = [];
  selectedTimeSlots: SpecificBookingSlot[] = [];
  isMinimumPlayingTimeCovered: boolean = false;
  minimumPlayingTime: number = 0;
  courts: CourtType[] = [
    {
      id: CourtId.Indoor,
      title: "Indoor",
    },
    {
      id: CourtId.Outdoor,
      title: "Outdoor",
    },
    {
      id: CourtId.Singles,
      title: "Singles",
    },
    {
      id: CourtId.IndoorLegacy,
      title: "Indoor Legacy",
    },
  ];
  selectedCourt: CourtType = this.courts[0];
  countries: Country[] = [];
  firstCountry: Country | null = null;
  secondCountry: Country | null = null;
  selectedLocation: CourtLocation | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    reaction(
      () => this.selectedDate,
      (current) => {
        if (current !== null) {
          this.fetchTimeSlots();
        }
      }
    );

    reaction(
      () => this.selectedTimeSlots,
      (current) => {
        if (current !== null) {
          this.setIsMinimumPlayingTimeCovered(
            this.selectedTimeSlots,
            this.minimumPlayingTime
          );
        }
      }
    );

    reaction(
      () => ({
        court: this.selectedCourt,
        date: this.timeSlotsResponse,
      }),
      (_current) => {
        this.clearSelectedTimeSlots();
        if (this.timeSlotsResponse !== null) {
          switch (this.selectedCourt.id) {
            case CourtId.Indoor:
              this.timeSlots =
                this.timeSlotsResponse?.availableIndoorSlots?.bookingSlots ??
                [];
              this.minimumPlayingTime =
                this.timeSlotsResponse?.availableIndoorSlots
                  ?.minimumPlayingTime ?? 0;
              break;
            case CourtId.Outdoor:
              this.timeSlots =
                this.timeSlotsResponse?.availableOutdoorSlots?.bookingSlots ??
                [];
              this.minimumPlayingTime =
                this.timeSlotsResponse?.availableOutdoorSlots
                  ?.minimumPlayingTime ?? 0;
              break;
            case CourtId.Singles:
              this.timeSlots =
                this.timeSlotsResponse?.availableSingleSlots?.bookingSlots ??
                [];
              this.minimumPlayingTime =
                this.timeSlotsResponse?.availableSingleSlots
                  ?.minimumPlayingTime ?? 0;
              break;
            case CourtId.IndoorLegacy:
              this.timeSlots =
                this.timeSlotsResponse?.availableIndoorLegacySlots
                  ?.bookingSlots ?? [];
              this.minimumPlayingTime =
                this.timeSlotsResponse?.availableIndoorLegacySlots
                  ?.minimumPlayingTime ?? 0;
              break;
          }
        }
      }
    );
  }

  setSelectedCourt(court: CourtType) {
    this.selectedCourt = court;
  }
  clearSelectedTimeSlots = () => {
    this.selectedTimeSlots = [];
  };

  reset = () => {
    this.modalData = undefined;
    this.isLoading = false;
    this.nextSixDays = [];
    this.selectedDate = null;
    this.modalData = undefined;
    this.timeSlotsResponse = null;
    this.timeSlots = [];
    this.selectedTimeSlots = [];
    this.isMinimumPlayingTimeCovered = false;
    this.minimumPlayingTime = 0;
    this.selectedCourt = this.courts[0];
    this.firstCountry = null;
    this.secondCountry = null;
    this.selectedLocation = null;
    // Regenerate the next six days
    this.generateNextSixDays();
  };

  setSelectedDate = (dateInfo: DateInfo) => {
    this.selectedDate = dateInfo;
  };

  setFirstCountry = (country: Country | null) => {
    this.firstCountry = country;
  };

  setSelectedLocation = (location: CourtLocation | null) => {
    this.selectedLocation = location;
  };

  setSecondCountry = (country: Country | null) => {
    this.secondCountry = country;
  };

  isButtonDisabled = () => {
    return (
      !this.selectedDate ||
      !this.selectedCourt ||
      !this.firstCountry ||
      !this.secondCountry ||
      !this.isMinimumPlayingTimeCovered ||
      this.selectedTimeSlots.length === 0 ||
      !this.selectedLocation
    );
  };

  getAllCountries = async () => {
    this.isLoading = true;
    try {
      const response = await FrontDeskApi.getCountries();
      this.countries = response;
    } catch (err) {
      console.log(err);
      this.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: "Error occurred while fetching countries",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.modalData = undefined),
      };
    } finally {
      this.isLoading = false;
    }
  };

  setIsMinimumPlayingTimeCovered = (
    items: SpecificBookingSlot[],
    minimumPlayingTime: number
  ) => {
    const totalDuration = this.calculateTotalDuration(items);
    this.isMinimumPlayingTimeCovered = totalDuration >= minimumPlayingTime;
  };

  calculateTotalDuration = (items: SpecificBookingSlot[]) => {
    let totalDuration = 0;
    items.forEach((item) => {
      const duration = item.endSlotTime - item.startSlotTime;
      totalDuration += duration;
    });
    return totalDuration;
  };

  generateNextSixDays = () => {
    this.nextSixDays = Array.from(
      { length: REQUEST_PLAYER_DATES_LIST_COUNT },
      (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() + i);

        return {
          date: date.getDate().toString(),
          day: days[date.getDay()],
          month: months[date.getMonth()],
          fullDate: new Date(date),
        };
      }
    );
  };

  fetchTimeSlots = async () => {
    try {
      this.isLoading = true;
      const response = await FrontDeskApi.getSpecificBookingSlots(
        this.selectedCourt.id,
        getDateInYYYYMMDDFormat(this.selectedDate?.fullDate ?? new Date())
      );
      this.timeSlotsResponse = response;
    } catch (err) {
      console.log(err);
      this.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: "Error occurred while fetching time slots",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onClose: () => (this.modalData = undefined),
      };
      this.resetAllTimesSlotsValues();
    } finally {
      this.isLoading = false;
    }
  };

  resetAllTimesSlotsValues = () => {
    this.timeSlotsResponse = null;
    this.timeSlots = [];
    this.selectedTimeSlots = [];
    this.isMinimumPlayingTimeCovered = false;
    this.minimumPlayingTime = 0;
  };

  getCalenderMinDate = () => {
    const date = new Date();
    date.setTime(
      this.nextSixDays[
        REQUEST_PLAYER_DATES_LIST_COUNT - 1
      ]?.fullDate.getTime() || 0
    );
    date.setDate(date.getDate() + 1);
    return date;
  };

  addCustomDate = (selectedDate: Date) => {
    const customDateInfo = {
      date: selectedDate.getDate().toString(),
      day: days[selectedDate.getDay()],
      month: months[selectedDate.getMonth()],
      fullDate: new Date(selectedDate),
    };

    // Check if this date already exists in the array
    const dateExists = this.nextSixDays.some(
      (dateInfo) =>
        dateInfo.fullDate.toDateString() ===
        customDateInfo.fullDate.toDateString()
    );

    if (!dateExists) {
      // Add the custom date and sort the array by date
      if (this.nextSixDays.length > REQUEST_PLAYER_DATES_LIST_COUNT) {
        this.nextSixDays[REQUEST_PLAYER_DATES_LIST_COUNT] = customDateInfo;
      } else {
        this.nextSixDays.push(customDateInfo);
      }
      this.nextSixDays.sort(
        (a, b) => a.fullDate.getTime() - b.fullDate.getTime()
      );
      this.setSelectedDate(customDateInfo);
    }
  };

  setSelectedTimeSlot = (slot: SpecificBookingSlot) => {
    // If no slots are selected, add the first one
    var slots = [...this.selectedTimeSlots];
    if (this.selectedTimeSlots.length === 0) {
      slots.push(slot);
      this.selectedTimeSlots = slots;
      return;
    }

    const lastSelectedSlot = slots[slots.length - 1];
    const firstSelectedSlot = slots[0];

    if (this.isContinuousTimeSlot(lastSelectedSlot, firstSelectedSlot, slot)) {
      slots.push(slot);
    } else {
      // Reset selection if non-continuous slot is selected
      slots = [slot];
      // this.modalData = {
      //   visible: true,
      //   title: Strings.WARNING,
      //   children: "Please select continuous time slots",
      //   btnText: Strings.OK,
      //   btnVariant: "outline-danger",
      //   onClose: () => (this.modalData = undefined),
      // };
    }
    this.selectedTimeSlots = slots.sort(
      (a, b) => a.startSlotTime - b.startSlotTime
    );
  };

  isContinuousTimeSlot = (
    lastSelected: SpecificBookingSlot,
    nextSelected: SpecificBookingSlot,
    item: SpecificBookingSlot
  ) => {
    const comparisonBar = item.endSlotTime - item.startSlotTime;
    return (
      (Math.abs(lastSelected.endSlotTime - item.endSlotTime) ===
        comparisonBar &&
        Math.abs(lastSelected.startSlotTime - item.startSlotTime) ===
          comparisonBar) ||
      (Math.abs(nextSelected.endSlotTime - item.endSlotTime) ===
        comparisonBar &&
        Math.abs(nextSelected.startSlotTime - item.startSlotTime) ===
          comparisonBar)
    );
  };

  postLeagueMatch = async (leagueId: number, onSuccess: () => void) => {
    try {
      this.isLoading = true;
      const payload: CreateLeagueMatchPayload = {
        countryAId: this.firstCountry?.id ?? 0,
        countryBId: this.secondCountry?.id ?? 0,
        bookingDate: getDateInYYYYMMDDFormat(
          this.selectedDate?.fullDate ?? new Date()
        ),
        startSlotTime: this.selectedTimeSlots[0].startSlotTime,
        endSlotTime:
          this.selectedTimeSlots[this.selectedTimeSlots.length - 1].endSlotTime,
        leagueId: leagueId,
        courtType: this.selectedCourt.id,
        locationId: this.selectedLocation?.locationId ?? 0,
      };

      await FrontDeskApi.createLeagueMatch(payload);
      this.isLoading = false;
      this.modalData = {
        visible: true,
        title: Strings.SUCCESS,
        children: "League match created successfully",
        btnText: Strings.OK,
        btnVariant: "outline-success",
        onAccept: () => {
          this.modalData = undefined;
          this.reset();
          onSuccess();
        },
      };
    } catch (err: any) {
      console.log(err);
      this.modalData = {
        visible: true,
        title: Strings.WARNING,
        children: err.message ?? "Error occurred while creating league match",
        btnText: Strings.OK,
        btnVariant: "outline-danger",
        onAccept: () => (this.modalData = undefined),
      };
      this.isLoading = false;
    }
  };
}
